import { AppstoreAddOutlined, AppstoreOutlined, DeleteOutlined } from '@ant-design/icons'
import { getOperationName } from '@apollo/client/utilities'
import { useToggle } from 'ahooks'
import { Avatar, Button, Image, Modal, Popover, Typography, message } from 'antd'
import cls from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { UserHoverCard } from '@/components'
import {
  GetFavoredKnowledgeGroupsDocument,
  RequestedKnowledgeGroupFragment,
  useKnowledgeGroupFavorToggleMutation,
} from '@/generated/graphql'
import { routes } from '@/routes'

import { KnowledgeCatalog } from '../knowledge-catalog/knowledge-catalog.component'
import s from './knowledge-card.module.less'

export const KnowledgeGroupFavorCard: React.FC<{
  knowledgeGroup?: RequestedKnowledgeGroupFragment & { posts: { totalCount: number } }
}> = ({ knowledgeGroup }) => {
  let content: React.ReactNode
  const history = useHistory()
  const { t } = useTranslation()
  const [visible, { toggle }] = useToggle()
  const [isHover, setIsHover] = React.useState<boolean>(false)

  const [knowledgeGroupFavorToggle] = useKnowledgeGroupFavorToggleMutation({
    refetchQueries: [getOperationName(GetFavoredKnowledgeGroupsDocument)!],
  })

  const onClick = () => {
    if (!knowledgeGroup) {
      toggle()
    }
  }

  if (knowledgeGroup) {
    const deleteIcon = (
      <Button
        shape='circle'
        icon={<DeleteOutlined />}
        onClick={async (e) => {
          e.stopPropagation()
          await knowledgeGroupFavorToggle({ variables: { knowledgeGroupId: knowledgeGroup.knowledgeGroupId } })
        }}
      />
    )

    const iconBar = (
      <div className={s.iconBar}>
        <div className={s.iconAction} onMouseEnter={() => setIsHover(true)} onMouseLeave={() => setIsHover(false)}>
          <div className={cls(s.delete, { [s.isHover]: isHover })}>{deleteIcon}</div>
        </div>
        <div className={s.titleIcon}>
          <Avatar size={48} shape='circle' icon={<AppstoreOutlined />} />
        </div>
      </div>
    )

    const image = (
      <div className={s.image}>
        <Image preview={false} />
      </div>
    )

    const title = (
      <div className={s.header}>
        <Typography.Title className={s.title} level={5}>
          {knowledgeGroup.name}
        </Typography.Title>
      </div>
    )

    const description = (
      <div className={s.description}>
        <Popover content={knowledgeGroup.description || t('knowledge.none')}>
          <span style={{ cursor: 'pointer' }}>{t('knowledge.knowledgeInfo')}</span>
        </Popover>
      </div>
    )

    const info = (
      <div className={s.info}>
        <span>{`${t('knowledge.count')}${t('colon')}${knowledgeGroup.posts.totalCount}`}</span>
        <div className={s.maintainer}>
          <span>{`${t('knowledge.maintainer')}${t('colon')}`}</span>
          <Avatar size={24} src={knowledgeGroup.creator.avatarUrl} />
          <UserHoverCard email={knowledgeGroup.creator.email}>
            <span
              className={s.maintainerName}
              onClick={(e) => {
                e.stopPropagation()
                history.push(routes.users.page(knowledgeGroup.creatorUserId))
              }}
            >
              {knowledgeGroup.creator.name}
            </span>
          </UserHoverCard>
        </div>
      </div>
    )

    content = (
      <div
        className={s.cardInner}
        onClick={() => history.push(routes.knowledgebase.category(knowledgeGroup.knowledgeGroupId))}
      >
        {image}
        {iconBar}
        <div className={s.content}>
          {title}
          {description}
          {info}
        </div>
      </div>
    )
  } else {
    content = (
      <div className={s.createContent} onClick={() => onClick()}>
        <AppstoreAddOutlined />
        <Typography.Text>添加收藏</Typography.Text>
      </div>
    )
  }

  return (
    <div className={cls(s.favorCard, { actionCard: !knowledgeGroup })}>
      <KnowledgeGroupFavorCardCreateModal visible={visible} toggle={toggle} />
      {content}
    </div>
  )
}

export const KnowledgeGroupFavorCardCreateModal: React.FC<{
  visible: boolean
  toggle: (value?: boolean | undefined) => void
}> = ({ visible, toggle }) => {
  const { t } = useTranslation()
  const [favorGroupKey, setFavorGroupKey] = React.useState<number>()

  const [knowledgeGroupFavorToggle] = useKnowledgeGroupFavorToggleMutation({
    refetchQueries: [getOperationName(GetFavoredKnowledgeGroupsDocument)!],
  })

  const onOK = async () => {
    if (!favorGroupKey) {
      message.warn('尚未选择要收藏的知识库')
      return
    }
    await knowledgeGroupFavorToggle({
      variables: {
        knowledgeGroupId: favorGroupKey!,
      },
    })
    toggle()
  }

  return (
    <Modal
      title={t('knowledge.favorCatalog')}
      visible={visible}
      onCancel={() => toggle()}
      onOk={() => onOK()}
      destroyOnClose
    >
      <KnowledgeCatalog favorGroupKey={favorGroupKey} setFavorGroupKey={setFavorGroupKey} type='favor' />
    </Modal>
  )
}
